import { GridCellParams } from '@mui/x-data-grid-pro'
import React from 'react'
import { useSelector } from 'react-redux'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  peopleInvitedToOrgDetailUrl,
  peopleMembersDetailUrl,
} from 'src/navigation/urls'
import {
  CardFeedAssignedUser,
  CardFeedCard,
  toCardFeedAvatarUser,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface CardFeedCardholderCellProps {
  assignedUser: CardFeedAssignedUser
  params: GridCellParams<CardFeedCard>
}

// DataGridCell's `.text` span truncates inline text with an ellipsis, but a
// HoverableLink renders `display: inline-flex` (an atomic inline box), so the
// parent's ellipsis can't apply and the link clips mid-character. Force the
// inner anchor to truncate itself instead — the descendant `a` selector
// outranks HoverableLink's own single-class rule, so this wins regardless of
// stylesheet order.
const linkTruncate = css`
  display: block;
  overflow: hidden;

  a {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

/**
 * Renders the assigned card owner as an avatar view inside the Card Holder
 * column: avatar + a name linked to the user's detail page (the linked-name
 * pattern from CardInformationBox's "Account Owner" field). The link resolves
 * for every membership status — an invited (not-yet-member) user lands on their
 * invitee page. A deactivated (removed) member is greyed via the avatar's
 * deactivated state (see toCardFeedAvatarUser).
 */
const CardFeedCardholderCell: React.FC<CardFeedCardholderCellProps> = ({
  assignedUser,
  params,
}): React.ReactElement => {
  const translate = useTranslate()
  const currentUserId = useSelector(selectUserIdFromState)
  const avatarUser = toCardFeedAvatarUser(assignedUser)

  const name =
    assignedUser.id === currentUserId
      ? translate('${userName} (you)', { userName: avatarUser.fullNameOrEmail })
      : avatarUser.fullNameOrEmail

  // An INVITED user has a pending org invite (not yet a member), so their detail
  // page lives under /people/invited; members (ACTIVE/DEACTIVATED) resolve to
  // the members route.
  const detailUrl =
    assignedUser.membershipStatus === 'INVITED'
      ? peopleInvitedToOrgDetailUrl(assignedUser.id)
      : peopleMembersDetailUrl(assignedUser.id)

  return (
    <DataGridCell
      params={params}
      icon={
        <UserAvatar
          css={css`
            display: flex;
            margin: 2px 0;
          `}
          user={avatarUser}
          size={AvatarSizes.Small}
          // The linked name beside it already announces the user — keep the
          // avatar decorative so screen readers don't repeat the name.
          decorative
        />
      }
      text={
        <span css={linkTruncate}>
          <HoverableLink
            to={detailUrl}
            underline
            ariaLabel={name}
            testId="card-feed-cardholder-link"
          >
            {name}
          </HoverableLink>
        </span>
      }
    />
  )
}

export default CardFeedCardholderCell
